import { useEffect, useMemo, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import {
  useListClasses, useCreateClass, useUpdateClass, useDeleteClass,
  useGetClassStudents, useListStudents, useAddStudentToClass, useRemoveStudentFromClass,
  useListCourses, getGetClassStudentsQueryKey,
  useUpdateClassPrefs, useReorderClasses, useArchiveClass, useGetProfile,
  getClassDependentsCount,
} from "@workspace/api-client-react";
import {
  Plus, Pencil, UsersRound, UserPlus, BookOpen, Check,
  Eye, EyeOff, GripVertical, Archive, ArchiveRestore, AlertTriangle,
} from "lucide-react";
import {
  DndContext, closestCenter, KeyboardSensor, PointerSensor,
  useSensor, useSensors, type DragEndEvent,
} from "@dnd-kit/core";
import {
  arrayMove, SortableContext, sortableKeyboardCoordinates,
  useSortable, verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { useToast } from "@/hooks/use-toast";
import { getApiErrorMessage } from "@/lib/api-error";
import {
  formatStudentName,
  studentSortKey,
  useDefaultStudentDisplayMode,
  type Student as DisplayStudent,
} from "@/lib/studentDisplay";

type ClassItem = {
  id: number;
  name: string;
  yearLevel?: string | null;
  schoolYear?: string | null;
  courseIds?: number[];
  studentCount: number;
  hidden: boolean;
  sortOrder?: number | null;
  archived?: boolean;
  schoolId?: number | null;
};
type Course = { id: number; name: string; color: string };

function ClassForm({ initial, onSave, onClose, isPending, courses }: {
  initial?: Partial<ClassItem>;
  onSave: (data: { name: string; yearLevel: string | null; schoolYear: string | null; courseIds: number[] }) => void;
  onClose: () => void;
  isPending: boolean;
  courses: Course[];
}) {
  const [name, setName] = useState(initial?.name ?? "");
  const [yearLevel, setYearLevel] = useState(initial?.yearLevel ?? "");
  const [schoolYear, setSchoolYear] = useState(initial?.schoolYear ?? "");
  const [courseIds, setCourseIds] = useState<Set<number>>(
    new Set(initial?.courseIds ?? []),
  );

  const toggle = (id: number) => {
    setCourseIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSave({
      name,
      yearLevel: yearLevel.trim() || null,
      schoolYear: schoolYear.trim() || null,
      courseIds: Array.from(courseIds),
    });
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div className="space-y-1.5">
        <Label>Class Name *</Label>
        <Input
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="e.g. 10.1, 10A, Year 10 Blue"
          required
        />
      </div>
      <div className="space-y-1.5">
        <Label>Year Level</Label>
        <Input
          value={yearLevel}
          onChange={(e) => setYearLevel(e.target.value)}
          placeholder="e.g. 10"
        />
      </div>
      <div className="space-y-1.5">
        <Label>School year</Label>
        <Input
          id="class-school-year"
          value={schoolYear}
          onChange={(e) => setSchoolYear(e.target.value)}
          placeholder="e.g. 2025, 2025/26"
        />
      </div>
      <div className="space-y-1.5">
        <Label>Subjects</Label>
        {courses.length === 0 ? (
          <p className="text-xs text-gray-400 border border-dashed rounded-md p-3 text-center">
            No subjects yet. You can create subjects later and link them to this class if useful.
          </p>
        ) : (
          <div className="border rounded-md max-h-44 overflow-y-auto divide-y" data-testid="course-checkbox-list">
            {courses.map((c) => {
              const checked = courseIds.has(c.id);
              return (
                <button
                  type="button"
                  key={c.id}
                  onClick={() => toggle(c.id)}
                  className={`w-full flex items-center gap-2 px-3 py-2 text-left hover:bg-gray-50 ${checked ? "bg-blue-50/40" : ""}`}
                  data-testid={`course-toggle-${c.id}`}
                >
                  <span
                    className={`w-4 h-4 rounded border flex items-center justify-center flex-shrink-0 ${checked ? "bg-blue-600 border-blue-600" : "border-gray-300"}`}
                  >
                    {checked && <Check className="h-3 w-3 text-white" />}
                  </span>
                  <span
                    className="inline-block w-2.5 h-2.5 rounded-full flex-shrink-0"
                    style={{ backgroundColor: c.color }}
                  />
                  <span className="text-sm">{c.name}</span>
                </button>
              );
            })}
          </div>
        )}
        <p className="text-xs text-gray-400">
          Tick the subjects this class covers. Optional — a class can link to multiple subjects.
          Teaching the same subject to more than one class? Create one Subject and link your Classes to it to reduce double-handling in planning and lesson prep.
        </p>
      </div>
      <DialogFooter>
        <Button type="button" variant="outline" onClick={onClose}>Cancel</Button>
        <Button type="submit" disabled={isPending} data-write-action>{isPending ? "Saving..." : "Save Class"}</Button>
      </DialogFooter>
    </form>
  );
}

function ClassStudentsPanel({ classId, onClose }: { classId: number; onClose: () => void }) {
  const { data: classStudents } = useGetClassStudents(classId);
  const { data: allStudents } = useListStudents();
  const addStudent = useAddStudentToClass();
  const removeStudent = useRemoveStudentFromClass();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [search, setSearch] = useState("");
  const accountDisplayMode = useDefaultStudentDisplayMode();

  const classStudentIds = new Set(classStudents?.map((s) => s.id) ?? []);
  const available = (allStudents ?? [])
    .filter((s) => {
      if (classStudentIds.has(s.id)) return false;
      const haystack = [s.firstName, s.lastName, (s as any).preferredName, (s as any).studentCode]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();
      return haystack.includes(search.toLowerCase());
    })
    .slice()
    .sort((a, b) =>
      studentSortKey(a as DisplayStudent).localeCompare(studentSortKey(b as DisplayStudent)),
    );

  const invalidateClassStudents = () =>
    queryClient.invalidateQueries({ queryKey: getGetClassStudentsQueryKey(classId) });

  const handleAdd = async (studentId: number) => {
    try {
      await addStudent.mutateAsync({ classId, data: { studentId } });
      invalidateClassStudents();
      toast({ title: "Student added to class" });
    } catch (err) {
      toast({
        title: "Couldn't add the student",
        description: getApiErrorMessage(err, "Please try again."),
        variant: "destructive",
      });
    }
  };

  const handleRemove = async (studentId: number) => {
    await removeStudent.mutateAsync({ classId, studentId });
    invalidateClassStudents();
    toast({ title: "Student removed" });
  };

  return (
    <div className="space-y-4">
      <div>
        <p className="text-sm font-semibold text-gray-700 mb-2">
          Enrolled Students ({classStudents?.length ?? 0})
        </p>
        <div className="border rounded-lg divide-y max-h-40 overflow-y-auto">
          {(classStudents ?? [])
            .slice()
            .sort((a, b) =>
              studentSortKey(a as DisplayStudent).localeCompare(studentSortKey(b as DisplayStudent)),
            )
            .map((s) => (
            <div key={s.id} className="flex items-center justify-between px-3 py-2">
              <span className="text-sm">{formatStudentName(s as DisplayStudent, accountDisplayMode)}</span>
              <Button
                variant="ghost"
                size="sm"
                className="text-xs text-red-500 h-7 px-2"
                onClick={() => handleRemove(s.id)}
               data-write-action>
                Remove
              </Button>
            </div>
          ))}
          {classStudents?.length === 0 && (
            <p className="p-4 text-xs text-center text-gray-400">No students enrolled</p>
          )}
        </div>
      </div>
      <div>
        <p className="text-sm font-semibold text-gray-700 mb-2">Add Students</p>
        <Input
          className="mb-2"
          placeholder="Search students..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
        <div className="border rounded-lg divide-y max-h-40 overflow-y-auto">
          {available.slice(0, 15).map((s) => (
            <div key={s.id} className="flex items-center justify-between px-3 py-2">
              <span className="text-sm">{formatStudentName(s as DisplayStudent, accountDisplayMode)}</span>
              <Button
                variant="ghost"
                size="sm"
                className="text-xs text-blue-600 h-7 px-2"
                onClick={() => handleAdd(s.id)}
               data-write-action>
                <UserPlus className="h-3 w-3 mr-1" />Add
              </Button>
            </div>
          ))}
          {available.length === 0 && (
            <p className="p-4 text-xs text-center text-gray-400">No more students to add</p>
          )}
        </div>
      </div>
      <DialogFooter><Button variant="outline" onClick={onClose}>Close</Button></DialogFooter>
    </div>
  );
}

// Sortable card. Drag handle is a dedicated grip — clicking the body still
// works for everything else.
function ClassCard({
  cls,
  courseMap,
  onEdit,
  onToggleHidden,
  onManageStudents,
  onArchive,
  hiddenPending,
  archivePending,
  canArchive,
}: {
  cls: ClassItem;
  courseMap: Map<number, Course>;
  onEdit: (c: ClassItem) => void;
  onToggleHidden: (c: ClassItem) => void;
  onManageStudents: (id: number) => void;
  onArchive: (c: ClassItem) => void;
  hiddenPending: boolean;
  archivePending: boolean;
  canArchive: boolean;
}) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } =
    useSortable({ id: cls.id });

  const linkedCourses = (cls.courseIds ?? [])
    .map((id) => courseMap.get(id))
    .filter((c): c is NonNullable<typeof c> => !!c);

  return (
    <div
      ref={setNodeRef}
      style={{
        transform: CSS.Transform.toString(transform),
        transition,
        opacity: isDragging ? 0.5 : cls.hidden ? 0.6 : 1,
      }}
      className="bg-white border rounded-xl p-5 shadow-sm hover:shadow-md transition-shadow group"
      data-testid={`class-card-${cls.id}`}
    >
      <div className="flex items-start justify-between">
        <div className="flex items-start gap-2 flex-1 min-w-0">
          <button
            {...attributes}
            {...listeners}
            className="mt-1 text-gray-300 hover:text-gray-500 cursor-grab active:cursor-grabbing touch-none"
            aria-label="Drag to reorder"
            data-testid={`drag-handle-class-${cls.id}`}
          >
            <GripVertical className="h-4 w-4" />
          </button>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <h3 className="font-bold text-gray-900 text-lg truncate">{cls.name}</h3>
              {cls.hidden && (
                <Badge variant="outline" className="text-[10px] uppercase tracking-wide text-gray-500">
                  Hidden
                </Badge>
              )}
            </div>
            <div className="flex items-center gap-2 mt-1 flex-wrap">
              {cls.yearLevel && (
                <Badge variant="secondary" className="text-xs">Year {cls.yearLevel}</Badge>
              )}
              <span className="text-xs text-gray-500">{cls.studentCount ?? 0} students</span>
              {linkedCourses.map((course) => (
                <span
                  key={course.id}
                  className="inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full font-medium"
                  style={{ backgroundColor: `${course.color}20`, color: course.color }}
                >
                  <BookOpen className="h-3 w-3" />
                  {course.name}
                </span>
              ))}
            </div>
          </div>
        </div>
        <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
          <Button
            variant="ghost"
            size="icon"
            className="h-8 w-8 text-gray-400 hover:text-gray-700"
            onClick={() => onToggleHidden(cls)}
            disabled={hiddenPending}
            title={cls.hidden ? "Unhide" : "Hide from your view"}
            data-testid={`button-toggle-hidden-class-${cls.id}`}
          >
            {cls.hidden ? <Eye className="h-4 w-4" /> : <EyeOff className="h-4 w-4" />}
          </Button>
          <Button
            variant="ghost"
            size="icon"
            className="h-8 w-8 text-gray-400 hover:text-blue-600"
            onClick={() => onEdit(cls)}
           data-write-action>
            <Pencil className="h-4 w-4" />
          </Button>
          {canArchive && (
            <Button
              variant="ghost"
              size="icon"
              className="h-8 w-8 text-gray-400 hover:text-amber-600"
              onClick={() => onArchive(cls)}
              disabled={archivePending}
              title="Archive — hides from your active list. You can restore it later. Past records keep working."
              data-testid={`button-archive-class-${cls.id}`}
             data-write-action>
              <Archive className="h-4 w-4" />
            </Button>
          )}
        </div>
      </div>
      <Button
        variant="outline"
        size="sm"
        className="mt-4 gap-2 text-xs"
        onClick={() => onManageStudents(cls.id)}
      >
        <UserPlus className="h-3 w-3" />
        Manage Students
      </Button>
    </div>
  );
}

export default function Classes() {
  const queryClient = useQueryClient();
  const { data: classes, isLoading } = useListClasses();
  // Past classes view fetches with includeArchived=1 so we can list the
  // archived rows with an Unarchive action. Only enabled while the modal
  // is open to avoid a baseline second request on every page load.
  const [pastOpen, setPastOpen] = useState(false);
  const { data: archivedListRaw, refetch: refetchArchived } = useListClasses(
    { includeArchived: "1" },
    { query: { enabled: pastOpen } },
  );
  const { data: courses } = useListCourses();
  const createClass = useCreateClass();
  const updateClass = useUpdateClass();
  const deleteClass = useDeleteClass();
  const updatePrefs = useUpdateClassPrefs();
  const reorder = useReorderClasses();
  const archiveClass = useArchiveClass();
  // Profile drives the head-teacher check used to gate the Archive action
  // on school-owned rows. Private (schoolId == null) classes are owned by
  // the caller (the API filters by userId), so any teacher may archive.
  const { data: profile } = useGetProfile();
  const isHeadTeacher = profile?.schoolRole === "head_teacher";
  const canArchive = (cls: ClassItem) => !cls.schoolId || isHeadTeacher;
  const { toast } = useToast();
  const [isOpen, setIsOpen] = useState(false);
  const [editing, setEditing] = useState<ClassItem | null>(null);
  const [studentsClassId, setStudentsClassId] = useState<number | null>(null);
  const [showHidden, setShowHidden] = useState(false);

  const courseMap = useMemo(
    () => new Map((courses ?? []).map((c) => [c.id, c])),
    [courses],
  );

  // Server already orders by (sortOrder NULLS LAST, name). We just split into
  // visible vs hidden so the toggle works locally and the dnd list only
  // contains rows that are actually shown. The default GET /classes already
  // excludes archived; the local filter is defensive in case of cache leak.
  const allClasses = ((classes ?? []) as ClassItem[]).filter((c) => !c.archived);
  const hiddenCount = allClasses.filter((c) => c.hidden).length;
  const visible = showHidden ? allClasses : allClasses.filter((c) => !c.hidden);

  const archivedList = ((archivedListRaw ?? []) as ClassItem[]).filter(
    (c) => c.archived,
  );

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  const handleSave = async (data: { name: string; yearLevel: string | null; schoolYear: string | null; courseIds: number[] }) => {
    try {
      if (editing) {
        await updateClass.mutateAsync({ classId: editing.id, data });
        toast({ title: "Class updated" });
      } else {
        await createClass.mutateAsync({ data });
        toast({ title: "Class created" });
      }
      queryClient.invalidateQueries({ queryKey: ["/api/classes"] });
      setIsOpen(false);
      setEditing(null);
    } catch (err) {
      toast({
        title: editing ? "Couldn't update the class" : "Couldn't create the class",
        description: getApiErrorMessage(err, "Please try again."),
        variant: "destructive",
      });
    }
  };

  // Permanent-delete state. The modal opens from the Past classes panel.
  // We fetch dependents on open; when all counts are zero we collapse to
  // a single "Yes, delete" button (no type-to-confirm). Mirror of the
  // courses flow added in Task #83.
  const [permaDeleteTarget, setPermaDeleteTarget] = useState<ClassItem | null>(null);

  const handlePermanentDelete = async (cls: ClassItem) => {
    try {
      await deleteClass.mutateAsync({ classId: cls.id });
      queryClient.invalidateQueries({ queryKey: ["/api/classes"] });
      refetchArchived();
      setPermaDeleteTarget(null);
      toast({ title: `"${cls.name}" permanently deleted` });
    } catch (err) {
      toast({
        title: "Couldn't delete the class",
        description: getApiErrorMessage(err, "Please try again."),
        variant: "destructive",
      });
    }
  };

  const handleToggleHidden = async (cls: ClassItem) => {
    try {
      await updatePrefs.mutateAsync({
        classId: cls.id,
        data: { hidden: !cls.hidden },
      });
      queryClient.invalidateQueries({ queryKey: ["/api/classes"] });
      toast({ title: cls.hidden ? "Class unhidden" : "Class hidden from your view" });
    } catch (err) {
      toast({
        title: "Couldn't update the class",
        description: getApiErrorMessage(err, "Please try again."),
        variant: "destructive",
      });
    }
  };

  // Year-end archive: removes the class from every active picker
  // (markbook, record-new, daily-override, timetable settings) but keeps
  // historical references resolvable for records, periods, and exports.
  // Archive is the default destructive action when a teacher clicks the
  // archive icon on a class. It is non-destructive: the row stays so
  // every record, period, and override that referenced it keeps
  // resolving. The "Archived. You can restore it…" toast tells the
  // teacher how to undo it. Hard delete now lives behind a separate
  // explicit modal in the Past classes panel (Task #104).
  const handleArchive = async (cls: ClassItem) => {
    try {
      await archiveClass.mutateAsync({ classId: cls.id, data: { archived: true } });
      queryClient.invalidateQueries({ queryKey: ["/api/classes"] });
      if (pastOpen) refetchArchived();
      toast({
        title: "Archived. You can restore it from Past classes.",
      });
    } catch (err) {
      toast({
        title: "Couldn't archive the class",
        description: getApiErrorMessage(err, "Please try again."),
        variant: "destructive",
      });
    }
  };

  const handleUnarchive = async (cls: ClassItem) => {
    try {
      await archiveClass.mutateAsync({ classId: cls.id, data: { archived: false } });
      queryClient.invalidateQueries({ queryKey: ["/api/classes"] });
      refetchArchived();
      toast({ title: "Class restored" });
    } catch (err) {
      toast({
        title: "Couldn't restore the class",
        description: getApiErrorMessage(err, "Please try again."),
        variant: "destructive",
      });
    }
  };

  const handleDragEnd = async (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;

    const oldIndex = visible.findIndex((c) => c.id === active.id);
    const newIndex = visible.findIndex((c) => c.id === over.id);
    if (oldIndex === -1 || newIndex === -1) return;

    const reorderedVisible = arrayMove(visible, oldIndex, newIndex);

    // The reorder request must include every row in the user's view (including
    // hidden ones when shown) so their stored sort order is correct.
    let nextOrder: ClassItem[];
    if (showHidden) {
      nextOrder = reorderedVisible;
    } else {
      // Append the hidden ones at the end of the order so they keep stable
      // positions relative to each other.
      const hiddenList = allClasses.filter((c) => c.hidden);
      nextOrder = [...reorderedVisible, ...hiddenList];
    }

    // Optimistically update the cache so the drag feels instant.
    queryClient.setQueryData(["/api/classes"], nextOrder);

    try {
      await reorder.mutateAsync({
        data: { orderedIds: nextOrder.map((c) => c.id) },
      });
    } catch (err) {
      toast({
        title: "Couldn't save the new order",
        description: getApiErrorMessage(err, "Please try again."),
        variant: "destructive",
      });
    } finally {
      queryClient.invalidateQueries({ queryKey: ["/api/classes"] });
    }
  };

  return (
    <div className="p-4 sm:p-6 max-w-5xl mx-auto">
      <div className="flex items-center justify-between mb-6 gap-3 flex-wrap">
        <h1 className="text-2xl font-bold text-gray-900">Classes</h1>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            className="gap-1.5 text-xs"
            onClick={() => setPastOpen(true)}
            data-testid="button-open-past-classes"
          >
            <Archive className="h-3.5 w-3.5" />
            Past classes
          </Button>
          {hiddenCount > 0 && (
            <Button
              variant={showHidden ? "secondary" : "outline"}
              size="sm"
              className="gap-1.5 text-xs"
              onClick={() => setShowHidden((v) => !v)}
              data-testid="button-toggle-show-hidden-classes"
            >
              {showHidden ? <EyeOff className="h-3.5 w-3.5" /> : <Eye className="h-3.5 w-3.5" />}
              {showHidden ? "Hide hidden" : `Show hidden (${hiddenCount})`}
            </Button>
          )}
          <Button
            className="bg-pink-500 hover:bg-pink-600 text-white rounded-full h-11 px-5 shadow-md shadow-pink-500/20 gap-2"
            onClick={() => { setEditing(null); setIsOpen(true); }}
           data-write-action>
            <Plus className="h-4 w-4" />
            New Class
          </Button>
        </div>
      </div>

      {isLoading && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {[...Array(4)].map((_, i) => <div key={i} className="h-24 bg-gray-100 animate-pulse rounded-xl" />)}
        </div>
      )}

      {!isLoading && allClasses.length === 0 && (
        <div className="py-24 text-center border-2 border-dashed border-gray-200 rounded-xl">
          <UsersRound className="h-12 w-12 text-gray-300 mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-gray-700 mb-1">No classes yet</h3>
          <p className="text-gray-500 mb-4">Create classes to group students and link them to courses.</p>
          <Button onClick={() => setIsOpen(true)} data-write-action>Create First Class</Button>
        </div>
      )}

      {!isLoading && allClasses.length > 0 && visible.length === 0 && (
        <div className="py-16 text-center border-2 border-dashed border-gray-200 rounded-xl">
          <EyeOff className="h-10 w-10 text-gray-300 mx-auto mb-3" />
          <p className="text-sm text-gray-500">All your classes are hidden. Click "Show hidden" above to bring them back.</p>
        </div>
      )}

      {visible.length > 0 && (
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragEnd={handleDragEnd}
        >
          <SortableContext
            items={visible.map((c) => c.id)}
            strategy={verticalListSortingStrategy}
          >
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {visible.map((cls) => (
                <ClassCard
                  key={cls.id}
                  cls={cls}
                  courseMap={courseMap}
                  onEdit={(c) => { setEditing(c); setIsOpen(true); }}
                  onToggleHidden={handleToggleHidden}
                  onManageStudents={setStudentsClassId}
                  onArchive={handleArchive}
                  hiddenPending={updatePrefs.isPending}
                  archivePending={archiveClass.isPending}
                  canArchive={canArchive(cls)}
                />
              ))}
            </div>
          </SortableContext>
        </DndContext>
      )}

      <Dialog open={isOpen} onOpenChange={(v) => { setIsOpen(v); if (!v) setEditing(null); }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{editing ? "Edit Class" : "New Class"}</DialogTitle>
          </DialogHeader>
          <ClassForm
            initial={editing ?? undefined}
            onSave={handleSave}
            onClose={() => { setIsOpen(false); setEditing(null); }}
            isPending={createClass.isPending || updateClass.isPending}
            courses={courses ?? []}
          />
        </DialogContent>
      </Dialog>

      <Dialog open={pastOpen} onOpenChange={setPastOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Past classes</DialogTitle>
          </DialogHeader>
          {archivedList.length === 0 ? (
            <p className="text-sm text-gray-500 py-6 text-center">
              You haven't archived any classes yet. Use the archive icon on a class to move it here at the end of a year.
            </p>
          ) : (
            <div className="border rounded-lg divide-y max-h-96 overflow-y-auto" data-testid="past-classes-list">
              {archivedList.map((cls) => (
                <div
                  key={cls.id}
                  className="flex items-center justify-between px-3 py-2.5"
                  data-testid={`past-class-row-${cls.id}`}
                >
                  <div className="min-w-0">
                    <span className="text-sm font-medium">{cls.name}</span>
                    {cls.yearLevel && (
                      <span className="ml-2 text-xs text-gray-400">Year {cls.yearLevel}</span>
                    )}
                    <span className="ml-2 text-xs text-gray-400">
                      · {cls.studentCount} students
                    </span>
                  </div>
                  {canArchive(cls) && (
                    <div className="flex items-center gap-1">
                      <Button
                        variant="ghost"
                        size="sm"
                        className="text-xs gap-1.5 text-blue-600"
                        onClick={() => handleUnarchive(cls)}
                        disabled={archiveClass.isPending}
                        data-testid={`button-unarchive-class-${cls.id}`}
                       data-write-action>
                        <ArchiveRestore className="h-3.5 w-3.5" />
                        Restore
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="text-xs text-gray-400 hover:text-red-600"
                        onClick={() => setPermaDeleteTarget(cls)}
                        data-testid={`button-permadelete-class-${cls.id}`}
                      >
                        Permanently delete…
                      </Button>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setPastOpen(false)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={studentsClassId !== null} onOpenChange={(v) => { if (!v) setStudentsClassId(null); }}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>Manage Students</DialogTitle></DialogHeader>
          {studentsClassId !== null && (
            <ClassStudentsPanel classId={studentsClassId} onClose={() => setStudentsClassId(null)} />
          )}
        </DialogContent>
      </Dialog>

      {permaDeleteTarget && (
        <PermanentDeleteClassModal
          cls={permaDeleteTarget}
          onClose={() => setPermaDeleteTarget(null)}
          onConfirm={() => handlePermanentDelete(permaDeleteTarget)}
          pending={deleteClass.isPending}
        />
      )}
    </div>
  );
}

// Permanent-delete modal (Task #104). Mirrors PermanentDeleteCourseModal:
// fetches dependents on open and renders the type-to-confirm flow only
// when at least one dependent row would be orphaned. Zero-dependent
// classes get a single "Yes, delete" button so the teacher doesn't have
// to type the name for an obviously safe deletion.
function PermanentDeleteClassModal({
  cls,
  onClose,
  onConfirm,
  pending,
}: {
  cls: ClassItem;
  onClose: () => void;
  onConfirm: () => void;
  pending: boolean;
}) {
  const [counts, setCounts] = useState<{
    students: number;
    timetableSlots: number;
    dailyOverrideSlots: number;
    records: number;
  } | null>(null);
  const [loadError, setLoadError] = useState(false);
  const [typed, setTyped] = useState("");

  // Fetch on mount. The imperative call keeps the modal self-contained
  // and avoids racing with React Query's cache when the modal is
  // reopened for a different class.
  useEffect(() => {
    let cancelled = false;
    getClassDependentsCount(cls.id)
      .then((c) => {
        if (!cancelled) setCounts(c);
      })
      .catch(() => {
        if (!cancelled) setLoadError(true);
      });
    return () => {
      cancelled = true;
    };
  }, [cls.id]);

  const total =
    (counts?.students ?? 0) +
    (counts?.timetableSlots ?? 0) +
    (counts?.dailyOverrideSlots ?? 0) +
    (counts?.records ?? 0);
  const hasDependents = counts != null && total > 0;
  const requiresType = hasDependents;
  const typedMatches = typed.trim() === cls.name;

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-red-700">
            <AlertTriangle className="h-5 w-5" />
            Permanently delete "{cls.name}"?
          </DialogTitle>
        </DialogHeader>

        {!counts && !loadError && (
          <p className="text-sm text-gray-500 py-4" data-testid="permadelete-loading">
            Checking what depends on this class…
          </p>
        )}

        {loadError && (
          <p className="text-sm text-red-600 py-4">
            Couldn't load dependent counts. Close and try again.
          </p>
        )}

        {counts && !hasDependents && (
          <div className="py-2" data-testid="permadelete-no-dependents">
            <p className="text-sm text-gray-700">
              Nothing else in your account references this class. Deleting it now is safe — it can't be undone, but no other rows will be affected.
            </p>
          </div>
        )}

        {counts && hasDependents && (
          <div className="space-y-3 py-2" data-testid="permadelete-with-dependents">
            <p className="text-sm text-gray-700">
              This will permanently delete:
            </p>
            <ul className="text-sm text-gray-800 list-disc pl-5 space-y-0.5">
              {counts.students > 0 && (
                <li>
                  <strong>{counts.students}</strong> student link{counts.students === 1 ? "" : "s"} to this class
                </li>
              )}
              {counts.timetableSlots > 0 && (
                <li>
                  <strong>{counts.timetableSlots}</strong> timetable period{counts.timetableSlots === 1 ? "" : "s"} pointing at this class
                </li>
              )}
              {counts.dailyOverrideSlots > 0 && (
                <li>
                  <strong>{counts.dailyOverrideSlots}</strong> saved daily override{counts.dailyOverrideSlots === 1 ? "" : "s"}
                </li>
              )}
              {counts.records > 0 && (
                <li>
                  <strong>{counts.records}</strong> record{counts.records === 1 ? "" : "s"} attached to this class
                </li>
              )}
            </ul>
            <p className="text-sm text-red-700 font-medium">
              This cannot be undone. Archiving instead keeps the class out of pickers but preserves these references.
            </p>
            <div className="space-y-1.5 pt-1">
              <Label htmlFor="permadelete-class-confirm-input">
                Type <span className="font-mono">{cls.name}</span> to confirm:
              </Label>
              <Input
                id="permadelete-class-confirm-input"
                value={typed}
                onChange={(e) => setTyped(e.target.value)}
                placeholder={cls.name}
                autoFocus
                data-testid="permadelete-confirm-input"
              />
            </div>
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={pending}>
            Cancel
          </Button>
          <Button
            variant="destructive"
            onClick={onConfirm}
            disabled={
              pending ||
              !counts ||
              loadError ||
              (requiresType && !typedMatches)
            }
            data-testid="permadelete-confirm-button"
            data-write-action
          >
            {pending ? "Deleting…" : "Yes, delete"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
